/**
 * SSE-based client for real-time notes streaming.
 *
 * Uses fetch + ReadableStream instead of EventSource to support
 * Authorization headers (EventSource doesn't support custom headers).
 */

import { getAuthToken } from "./api";
import type { NotesState, PatchEvent } from "./types";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

export interface SSECallbacks {
  onInit?: (data: NotesState) => void;
  onPatch?: (event: PatchEvent) => void;
  onError?: (error: Error | Event) => void;
  onOpen?: () => void;
}

/**
 * Parse SSE event from text line.
 * SSE format: "event: <type>\ndata: <json>\n\n"
 */
function parseSSEEvent(text: string): { event: string; data: string } | null {
  const lines = text.trim().split("\n");
  let event = "message";
  let data = "";

  for (const line of lines) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      data = line.slice(5).trim();
    }
  }

  if (!data) return null;
  return { event, data };
}

/**
 * Subscribe to real-time notes updates via SSE.
 * Uses fetch with Authorization header instead of EventSource with query params.
 *
 * @param contactId - The contact ID to subscribe to
 * @param callbacks - Event callbacks
 * @returns Unsubscribe function
 */
export function subscribeToNotes(
  contactId: string,
  callbacks: SSECallbacks
): () => void {
  const url = `${API_BASE}/api/notes/${contactId}/stream`;

  let abortController: AbortController | null = new AbortController();
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  let isClosing = false;
  let retryDelay = 5000;

  const connect = async () => {
    if (isClosing) return;

    try {
      abortController = new AbortController();
      const token = getAuthToken();

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: abortController.signal,
      });

      if ([401, 403, 404].includes(response.status)) {
        // These failures require a new session/contact, not an endless retry.
        isClosing = true;
        callbacks.onError?.(new Error(`SSE connection stopped: ${response.status}`));
        await response.body?.cancel();
        return;
      }
      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      console.log("[SSE] Connection opened");
      callbacks.onOpen?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log("[SSE] Stream ended");
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by double newlines
        const events = buffer.split("\n\n");
        buffer = events.pop() || ""; // Keep incomplete event in buffer

        for (const eventText of events) {
          if (!eventText.trim()) continue;

          const parsed = parseSSEEvent(eventText);
          if (parsed) retryDelay = 5000;
          if (!parsed) continue;

          try {
            if (parsed.event === "init") {
              const data = JSON.parse(parsed.data) as NotesState;
              callbacks.onInit?.(data);
            } else if (parsed.event === "patch") {
              const data = JSON.parse(parsed.data) as PatchEvent;
              callbacks.onPatch?.(data);
            }
          } catch (e) {
            console.error(`[SSE] Error parsing ${parsed.event} data:`, e);
          }
        }
      }

      // Stream ended, try to reconnect unless closing
      if (!isClosing) {
        reconnectTimeout = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 60000);
      }
    } catch (error) {
      if (isClosing || (error instanceof Error && error.name === "AbortError")) {
        return;
      }

      console.error("[SSE] Connection error:", error);
      callbacks.onError?.(error instanceof Error ? error : new Error(String(error)));

      // Retry connection after delay
      if (!isClosing) {
        reconnectTimeout = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 60000);
      }
    }
  };

  // Start connection
  connect();

  // Return unsubscribe function
  return () => {
    console.log("[SSE] Closing connection");
    isClosing = true;

    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };
}

/**
 * Legacy function for compatibility.
 * Use subscribeToNotes instead.
 */
export function subscribeToPatchs(
  contactId: string,
  onPatch: (patch: PatchEvent & { contactId: string; timestamp: number }) => void,
  onError?: (error: Error | Event) => void
): () => void {
  return subscribeToNotes(contactId, {
    onPatch: (event) => {
      onPatch({
        ...event,
        contactId,
        timestamp: Date.now(),
      });
    },
    onError,
  });
}
